import { extractDigits } from 'shared/utilities/digits';

const OTP_SESSION_KEY = 'pacilo.user.otp_session';

let memorySession = null;

function writeSession(payload) {
  memorySession = payload;
  try {
    if (!payload) {
      sessionStorage.removeItem(OTP_SESSION_KEY);
      return;
    }
    sessionStorage.setItem(OTP_SESSION_KEY, JSON.stringify(payload));
  } catch {
    // Ignore storage access failures.
  }
}

function readStoredSession() {
  try {
    const raw = sessionStorage.getItem(OTP_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function compactDraftProfile(draft) {
  if (!draft || typeof draft !== 'object' || Array.isArray(draft)) {
    return null;
  }
  return {
    full_name: draft.full_name != null ? String(draft.full_name) : '',
    age_range: draft.age_range != null ? String(draft.age_range) : '',
    education_level: draft.education_level != null ? String(draft.education_level) : '',
    is_employed: draft.is_employed === true || draft.is_employed === false ? draft.is_employed : null,
    work_experience: draft.work_experience != null ? String(draft.work_experience) : '',
    experience_field: draft.experience_field != null ? String(draft.experience_field) : '',
    skills: Array.isArray(draft.skills) ? draft.skills.map((item) => String(item)).filter(Boolean) : [],
    country: draft.country != null ? String(draft.country) : '',
  };
}

function compactLegal(document) {
  if (!document || typeof document !== 'object') {
    return null;
  }
  if (document.id == null) {
    return null;
  }
  return {
    id: document.id,
    version: document.version || null,
    type: document.type || null,
  };
}

export function saveOtpSession(session) {
  if (!session) {
    clearOtpSession();
    return null;
  }
  const current = getOtpSession() || {};
  const pendingCode = Object.prototype.hasOwnProperty.call(session, 'pending_code')
    ? extractDigits(session.pending_code, 6)
    : extractDigits(current.pending_code, 6);
  const payload = {
    phone: session.phone || current.phone,
    masked_phone: session.masked_phone || current.masked_phone,
    channel: session.channel || current.channel || 'sms',
    expires_in_seconds: Number(session.expires_in_seconds ?? current.expires_in_seconds) || 0,
    resend_after_seconds: Number(session.resend_after_seconds ?? current.resend_after_seconds) || 0,
    remaining_attempts: Number(session.remaining_attempts ?? current.remaining_attempts) || 0,
    terms: compactLegal(session.terms) || compactLegal(current.terms),
    privacy: compactLegal(session.privacy) || compactLegal(current.privacy),
    pending_code: pendingCode,
    draft_full_name: session.draft_full_name != null ? String(session.draft_full_name) : (current.draft_full_name || ''),
    draft_profile: compactDraftProfile(session.draft_profile != null ? session.draft_profile : current.draft_profile),
    started_at: session.started_at || current.started_at || Date.now(),
  };
  writeSession(payload);
  return payload;
}

export function getOtpSession() {
  return memorySession || readStoredSession();
}

export function setPendingOtpCode(code, extras = {}) {
  const session = getOtpSession();
  if (!session) {
    return null;
  }
  return saveOtpSession({
    ...session,
    ...extras,
    pending_code: extractDigits(code, 6),
  });
}

export function getPendingOtpCode() {
  return extractDigits(getOtpSession()?.pending_code, 6);
}

export function clearPendingOtpCode() {
  const session = getOtpSession();
  if (!session) {
    return null;
  }
  return saveOtpSession({
    ...session,
    pending_code: '',
  });
}

export function setDraftFullName(name) {
  return setDraftProfile({ full_name: String(name || '') });
}

export function setDraftProfile(draft) {
  const session = getOtpSession();
  if (!session) {
    return null;
  }
  const current = compactDraftProfile(session.draft_profile) || {};
  const next = compactDraftProfile({
    ...current,
    ...draft,
    full_name: draft?.full_name != null ? draft.full_name : (current.full_name || session.draft_full_name || ''),
  });
  return saveOtpSession({
    ...session,
    draft_full_name: next?.full_name || '',
    draft_profile: next,
  });
}

export function getDraftProfile() {
  const session = getOtpSession();
  if (!session) {
    return null;
  }
  return compactDraftProfile(session.draft_profile) || compactDraftProfile({
    full_name: session.draft_full_name || '',
  });
}

export function clearOtpSession() {
  writeSession(null);
}

export function remainingFrom(startedAt, totalSeconds) {
  const elapsed = Math.floor((Date.now() - Number(startedAt || Date.now())) / 1000);
  return Math.max(0, Number(totalSeconds || 0) - elapsed);
}
